/**
 * Passive Learner Mode
 *
 * Runs silently alongside WidgetMode on every page load.
 * Captures page snapshots, network calls, and click patterns — then
 * flushes data to the iraa.ai API for progressive PDP building.
 *
 * No UI, no user interaction, no impact on page performance.
 *
 * ── Cost Model (diff-only, 1 scan/day) ────────────────────────────
 *
 * The client stores all known route→contentHash pairs in localStorage.
 * On every page visit, it compares the live hash to the stored hash.
 * If unchanged → zero cost (no capture, no network).
 * If new or changed → buffer it, send on page unload.
 *
 * After the first scan cycle of the day, it stops capturing snapshots
 * and only collects click sequences (tiny payload, useful for flows).
 *
 * Net effect:
 * - Stable product, 1M users → ~0 API calls/day
 * - Product ships new /dashboard → 1 capture from first visitor
 * - New page /analytics added → 1 capture from first visitor
 * - Clicks always flow → new user flows discovered continuously
 */

import { capturePageSnapshot, type PageSnapshot } from '../capture/element-mapper.js';
import { generateSelector } from '../capture/selector-gen.js';
import { SPADetector } from '../capture/spa-detector.js';
import { normalizeRoute } from '../capture/route-normalizer.js';
import { NetworkInterceptor, type CapturedApiCall } from '../capture/network-interceptor.js';

// ─── Types ────────────────────────────────────────────────────────────────

export interface ClickEvent {
  selector: string;
  label: string;
  tag: string;
  route: string;
  timestamp: number;
}

export interface PageCapture {
  route: string;
  rawRoute: string;
  title: string;
  snapshot: PageSnapshot | null;
  apiCalls: CapturedApiCall[];
  clickSequence: ClickEvent[];
  timestamp: number;
  sessionHash: string;
}

interface PassiveLearnerConfig {
  apiUrl: string;
  apiKey: string;
}

// ─── LocalStorage Keys ───────────────────────────────────────────────────

const KEY_KNOWN_ROUTES = 'da_known_routes';   // { route: contentHash }
const KEY_LAST_SCAN = 'da_last_scan_date';    // "2026-04-09"

// ─── Helpers ──────────────────────────────────────────────────────────────

function generateSessionHash(): string {
  const arr = new Uint8Array(8);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

function hashSnapshot(snapshot: PageSnapshot): string {
  const sig = [
    snapshot.urlPath,
    snapshot.elements.length,
    snapshot.elements
      .slice(0, 20)
      .map((e) => `${e.type}:${e.label}`)
      .join('|'),
    snapshot.forms.length,
    snapshot.tables.length,
  ].join('::');

  let hash = 5381;
  for (let i = 0; i < sig.length; i++) {
    hash = ((hash << 5) + hash + sig.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

function getClickLabel(el: HTMLElement): string {
  return (
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    el.getAttribute('placeholder') ||
    el.innerText?.trim().slice(0, 60) ||
    ''
  );
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10); // "2026-04-09"
}

function loadKnownRoutes(): Map<string, string> {
  try {
    const stored = localStorage.getItem(KEY_KNOWN_ROUTES);
    if (!stored) return new Map();
    return new Map(Object.entries(JSON.parse(stored)));
  } catch {
    return new Map();
  }
}

function saveKnownRoutes(routes: Map<string, string>): void {
  try {
    localStorage.setItem(
      KEY_KNOWN_ROUTES,
      JSON.stringify(Object.fromEntries(routes)),
    );
  } catch { /* quota exceeded — non-critical */ }
}

function hasScannedToday(): boolean {
  try {
    return localStorage.getItem(KEY_LAST_SCAN) === todayStr();
  } catch {
    return false;
  }
}

function markScannedToday(): void {
  try {
    localStorage.setItem(KEY_LAST_SCAN, todayStr());
  } catch { /* non-critical */ }
}

// ─── Main Class ───────────────────────────────────────────────────────────

export class PassiveLearner {
  private config: PassiveLearnerConfig;
  private spaDetector: SPADetector | null = null;
  private networkInterceptor: NetworkInterceptor;
  private buffer: PageCapture[] = [];
  private currentClickSequence: ClickEvent[] = [];
  private sessionHash: string;
  private knownRoutes: Map<string, string>;
  private scanMode: boolean;    // true = capture snapshots, false = clicks only
  private started = false;

  constructor(config: PassiveLearnerConfig) {
    this.config = config;
    this.networkInterceptor = new NetworkInterceptor();
    this.sessionHash = generateSessionHash();
    this.knownRoutes = loadKnownRoutes();

    // Scan once per day per device. After that, clicks only.
    this.scanMode = !hasScannedToday();
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    // Always track clicks — tiny cost, valuable for flow discovery
    this.startClickTracking();

    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flush());

    if (this.scanMode) {
      // New day — capture snapshots + network calls
      this.networkInterceptor.start();

      // Capture current page after DOM settles, then flush
      setTimeout(() => {
        this.capturePage();
        // Flush after first capture so data reaches server immediately
        setTimeout(() => this.flush(), 3000);
      }, 2000);

      // Capture on SPA route changes + flush after each
      this.spaDetector = new SPADetector((_info) => {
        this.flushClickSequenceToLastCapture();
        // Flush previous page's data, then capture new page
        this.flush();
        setTimeout(() => {
          this.capturePage();
          // Flush the new capture after a short delay
          setTimeout(() => this.flush(), 3000);
        }, 1500);
      });
      this.spaDetector.start();
    }
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;

    this.flush();
    this.spaDetector?.stop();
    this.spaDetector = null;
    this.networkInterceptor.stop();
  }

  // ─── Page Capture ─────────────────────────────────────────────────────

  private capturePage(): void {
    if (!this.scanMode) return;

    try {
      const snapshot = capturePageSnapshot();
      const route = normalizeRoute(snapshot.urlPath);
      const contentHash = hashSnapshot(snapshot);

      // Skip if content hasn't changed since we last saw this route
      if (this.knownRoutes.get(route) === contentHash) return;

      // New or changed — update local cache and buffer
      this.knownRoutes.set(route, contentHash);
      saveKnownRoutes(this.knownRoutes);

      const apiCalls = this.networkInterceptor.drain();

      this.buffer.push({
        route,
        rawRoute: snapshot.urlPath,
        title: snapshot.pageTitle,
        snapshot,
        apiCalls,
        clickSequence: [],
        timestamp: Date.now(),
        sessionHash: this.sessionHash,
      });
    } catch (err) {
      console.debug('[DemoAgent] PassiveLearner capture error:', err);
    }
  }

  // ─── Click Tracking ───────────────────────────────────────────────────

  private startClickTracking(): void {
    document.addEventListener(
      'click',
      (e) => {
        const target = (e.target as HTMLElement)?.closest(
          'button, a, [role="button"], [role="tab"], [role="link"], input[type="submit"], [data-testid], [data-demo-id]',
        ) as HTMLElement | null;

        if (!target) return;

        try {
          this.currentClickSequence.push({
            selector: generateSelector(target),
            label: getClickLabel(target),
            tag: target.tagName.toLowerCase(),
            route: normalizeRoute(location.pathname),
            timestamp: Date.now(),
          });

          if (this.currentClickSequence.length > 50) {
            this.currentClickSequence = this.currentClickSequence.slice(-50);
          }
        } catch {
          // Silently ignore
        }
      },
      { capture: true, passive: true },
    );
  }

  private flushClickSequenceToLastCapture(): void {
    if (this.currentClickSequence.length === 0) return;

    const currentRoute = normalizeRoute(location.pathname);

    // Attach to existing capture for this route
    for (let i = this.buffer.length - 1; i >= 0; i--) {
      if (this.buffer[i].route === currentRoute) {
        this.buffer[i].clickSequence = [...this.currentClickSequence];
        this.currentClickSequence = [];
        return;
      }
    }

    // No matching capture — create a click-only entry if we have a meaningful flow
    if (this.currentClickSequence.length >= 2) {
      this.buffer.push({
        route: currentRoute,
        rawRoute: location.pathname,
        title: document.title,
        snapshot: null,
        apiCalls: [],
        clickSequence: [...this.currentClickSequence],
        timestamp: Date.now(),
        sessionHash: this.sessionHash,
      });
    }

    this.currentClickSequence = [];
  }

  // ─── Flush to Server ──────────────────────────────────────────────────

  private flush(): void {
    this.flushClickSequenceToLastCapture();

    if (this.buffer.length === 0) return;

    const captures = this.buffer;
    this.buffer = [];

    // Mark today as scanned so subsequent sessions skip snapshot capture
    if (this.scanMode) {
      markScannedToday();
    }

    const payload = JSON.stringify({ captures });
    const url = `${this.config.apiUrl}/api/v1/discover/ingest?key=${encodeURIComponent(this.config.apiKey)}`;

    console.debug(`[DemoAgent] Flushing ${captures.length} capture(s) to ${this.config.apiUrl}`);

    // Use fetch as primary — visible in Network tab for debugging.
    // Fall back to sendBeacon on page unload.
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    })
      .then((resp) => {
        if (!resp.ok) {
          resp.text().then((t) => console.warn('[DemoAgent] Ingest failed:', resp.status, t));
        } else {
          console.debug('[DemoAgent] Ingest OK');
        }
      })
      .catch((err) => {
        console.debug('[DemoAgent] Ingest error:', err);
      });
  }
}
